import React from 'react';
import type { SimWorld } from '../../simulation/types/SimWorld';
import { isGroupModeEnabled } from '../../simulation/types/SimWorld';
import type { GroupStats, GroupId } from '../../simulation/types/Group';

interface GroupStatsPanelProps {
  world: SimWorld;
  isMobile?: boolean;
}

export const GroupStatsPanel: React.FC<GroupStatsPanelProps> = ({ world, isMobile }) => {
  if (!isGroupModeEnabled(world) || !world.groupStats || !world.groupModeConfig) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>グループ統計</h3>
        <p style={styles.noData}>グループモードが無効です</p>
      </div>
    );
  }

  const groups = world.groupModeConfig.groups;
  const groupStatsArray = Array.from(world.groupStats.entries());

  // 勝率でソート
  const sortedStats = [...groupStatsArray].sort((a, b) => {
    const aWinRate = a[1].totalWins + a[1].totalLosses > 0
      ? a[1].totalWins / (a[1].totalWins + a[1].totalLosses)
      : 0.5;
    const bWinRate = b[1].totalWins + b[1].totalLosses > 0
      ? b[1].totalWins / (b[1].totalWins + b[1].totalLosses)
      : 0.5;
    return bWinRate - aWinRate;
  });

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>グループ統計</h3>

      {/* グループ一覧 */}
      <div style={isMobile ? styles.gridMobile : styles.grid}>
        {sortedStats.map(([groupId, stats], index) => {
          const group = groups.find(g => g.id === groupId);
          const groupName = group?.name || `グループ${groupId}`;
          const groupColor = group?.color || '#666';

          const totalGames = stats.totalWins + stats.totalLosses;
          const winRate = totalGames > 0
            ? ((stats.totalWins / totalGames) * 100).toFixed(1)
            : '-';

          return (
            <div
              key={groupId}
              style={{
                ...styles.groupCard,
                borderColor: groupColor,
              }}
            >
              {/* ランキングバッジ */}
              <div style={{
                ...styles.rankBadge,
                background: index === 0 ? '#ffd700' :
                           index === 1 ? '#c0c0c0' :
                           index === 2 ? '#cd7f32' : '#666',
              }}>
                #{index + 1}
              </div>

              {/* グループ名 */}
              <div style={{ ...styles.groupName, color: groupColor }}>
                {groupName}
              </div>

              {/* 統計 */}
              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>人口</span>
                  <span style={styles.statValue}>{stats.aliveCount}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>勝率</span>
                  <span style={styles.statValue}>{winRate}%</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>勝利</span>
                  <span style={{ ...styles.statValue, color: '#4caf50' }}>{stats.totalWins}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>敗北</span>
                  <span style={{ ...styles.statValue, color: '#f44336' }}>{stats.totalLosses}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>平均Rt</span>
                  <span style={styles.statValue}>{stats.averageRating}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>最高Rt</span>
                  <span style={{ ...styles.statValue, color: '#ffc107' }}>{stats.maxRating}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>出生</span>
                  <span style={{ ...styles.statValue, color: '#8bc34a' }}>{stats.birthCount}</span>
                </div>
                <div style={styles.statItem}>
                  <span style={styles.statLabel}>死亡</span>
                  <span style={{ ...styles.statValue, color: '#9e9e9e' }}>{stats.deathCount}</span>
                </div>
              </div>

              {/* vsグループ別成績 */}
              <div style={styles.vsSection}>
                <div style={styles.vsTitle}>vs グループ別</div>
                <div style={styles.vsGrid}>
                  {groups.filter(g => g.id !== groupId).map(opponent => {
                    const wins = stats.winsVsGroup.get(opponent.id) || 0;
                    const losses = stats.lossesVsGroup.get(opponent.id) || 0;
                    const vsTotal = wins + losses;
                    const vsWinRate = vsTotal > 0 ? ((wins / vsTotal) * 100).toFixed(0) : '-';

                    return (
                      <div key={opponent.id} style={styles.vsItem}>
                        <span style={{ color: opponent.color }}>{opponent.name}</span>
                        <span style={styles.vsRecord}>
                          {wins}勝{losses}敗 ({vsWinRate}%)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: '8px',
  },
  title: {
    margin: '0 0 16px 0',
    fontSize: '18px',
    color: '#fff',
    borderBottom: '2px solid #667eea',
    paddingBottom: '8px',
  },
  noData: {
    color: '#888',
    textAlign: 'center',
    padding: '20px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '16px',
  },
  gridMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  groupCard: {
    position: 'relative',
    background: '#2a2a3e',
    borderRadius: '8px',
    padding: '16px',
    border: '2px solid',
  },
  rankBadge: {
    position: 'absolute',
    top: '-8px',
    right: '-8px',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#000',
    fontWeight: 'bold',
    fontSize: '12px',
  },
  groupName: {
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '8px',
    marginBottom: '12px',
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '6px',
    background: '#1a1a2e',
    borderRadius: '4px',
  },
  statLabel: {
    fontSize: '10px',
    color: '#888',
    marginBottom: '2px',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#fff',
  },
  vsSection: {
    borderTop: '1px solid #444',
    paddingTop: '8px',
    marginTop: '4px',
  },
  vsTitle: {
    fontSize: '12px',
    color: '#888',
    marginBottom: '8px',
  },
  vsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  vsItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
    padding: '4px 8px',
    background: '#1a1a2e',
    borderRadius: '4px',
  },
  vsRecord: {
    color: '#aaa',
  },
};

export default GroupStatsPanel;
